import NameWarningsAlert from "../components/NameWarningsAlert";
import React, { PropsWithChildren, useEffect } from "react";
import { Alert, AlertTitle, Autocomplete, Box, Button, Container, Table, TableBody, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { z } from "zod";
import { Form, useActionData, useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { Controller, FieldPath, useForm } from "react-hook-form";
import { CheckboxElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { ActionFunction, LoaderFunction, redirect } from "react-router-dom";
import { REVIEW_PAGE_VERSION } from '../consentVersions'
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient";
import { queryClient } from "../bootstrapping/InitReactQuery";
import { QueryClient } from "@tanstack/react-query";
import { schemas } from "../generated/api/client";
import { LocalFormSchema } from "../logic/applicationUpdateSchema";
import * as datefns from "date-fns"
import FormButtons from "../components/FormButtons";
import { TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { fieldLabel, flattenErrors } from "../utils/forms";
import { applicationRefinement } from "../logic/dbsRules";
import { languagePreferenceMapping } from "../utils/option-data";
import { AlternativeValidDocuments } from "./ApplicationUpdateDocuments";
import { afterQueryParamKey } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";
import { useStartDatesOfAllAddresses } from "./ApplicationUpdateAddresses";

export const zCompleteFormSchema = schemas.ApplicantDetailsStructure.extend({
  _noOtherNames: z.boolean().optional(),
  _noOtherAddresses: z.boolean().optional(),
  _passportDetailsCorrect: z.boolean().optional(),
  _driverLicenceDetailsCorrect: z.boolean().optional(),
  _privacyPolicyConfirmed: z.boolean().optional(),
})
  .superRefine((value, context) => {
    const confirmationRequired = (path: string[]) => {
      context.addIssue({
        code: 'custom',
        message: 'Confimation is required',
        path,
      })
    }
    // Check the user has confirmed they have no other names or addresses
    if (value._noOtherNames !== true) confirmationRequired(['_noOtherNames'])
    if (value._noOtherAddresses !== true) confirmationRequired(['_noOtherAddresses'])
    // If the passport number is set, check for the confirmation
    console.log("zCompleteFormSchema.superRefine: ", { value, context })
    if (!!value.ApplicantIdentityDetails.PassportDetails?.PassportNumber && value._passportDetailsCorrect !== true) confirmationRequired(['_passportDetailsCorrect'])
    // If the driver licence number is set, check for the confirmation
    if (!!value.ApplicantIdentityDetails.DriverLicenceDetails?.DriverLicenceNumber && value._driverLicenceDetailsCorrect !== true) confirmationRequired(['_driverLicenceDetailsCorrect'])
    // They must confirm they have read the privacy policy
    if (value._privacyPolicyConfirmed !== true) confirmationRequired(['_privacyPolicyConfirmed'])
  })
  // Every rule the individual pages enforce, plus the cross-page ones (the document dates of birth, five years of
  // addresses, the declaration), so nothing a page let through can be submitted. The confirm screen is gate 4 (DBS-344).
  .superRefine(applicationRefinement('confirm'))
export type CompleteFormSchema = z.infer<typeof zCompleteFormSchema>

/**
 * Loads the EBulkApplication by ID and ensures it's in the ReactQuery cache before loading the page
 */
export const loader = (queryClient: QueryClient): LoaderFunction => async ({ params }) => {
  const { application_id } = z.object({ application_id: z.string() }).parse(params)
  const queryKey = [...apiClientHooks.getKeyByAlias('getEBulkApplicationByID'), { params: { id: application_id } }]
  return await queryClient.ensureQueryData({ queryKey, queryFn: () => apiClient.getEBulkApplicationByID({ params: { id: application_id } }) })
}

/**
 * Saves the EBulkApplication form data by ID
 */
export const action: ActionFunction = async ({ request, params, context }) => {
  const { application_id } = z.object({ application_id: z.string() }).parse(params)

  const formData = await request.formData()
  const formObject = Object.fromEntries(formData)
  // if formObject.eBulkApplicationJSON is a string, parse it as a JSON string
  const eBulkApplication = typeof formObject.eBulkApplicationJSON === 'string'
    ? JSON.parse(formObject.eBulkApplicationJSON)
    : undefined

  console.log("ApplicationUpdate.action: ", { request, params, context, formObject, eBulkApplication })

  if (!eBulkApplication) {
    console.info('No form data was submitted, skipping update.')
    return redirect(`/`)
  }

  // The schema carries the (async) business rules, so it must be parsed asynchronously here as well as in the resolver
  const parsedFormObject = await zCompleteFormSchema.parseAsync(eBulkApplication)

  delete parsedFormObject._noOtherNames
  delete parsedFormObject._noOtherAddresses
  delete parsedFormObject._passportDetailsCorrect
  delete parsedFormObject._driverLicenceDetailsCorrect

  // The privacy notice confirmation ticked on this page, under the wording this build shows (DBS-266)
  await apiClient.recordMyEBulkApplicationConsents(
    { screen: 'review_page', wording_version: REVIEW_PAGE_VERSION, kinds: ['review_privacy_notice'] },
    { params: { id: application_id } },
  )
  try {
    await apiClient.completeMyEBulkApplicationByID(parsedFormObject, { params: { id: application_id } })
  } catch (error) {
    // DBS-305: the server checks the application again as it is completed; show its reasons rather than an error page
    const refused = isAxiosError(error) ? schemas.EBulkApplicationGateRefused.safeParse(error.response?.data) : undefined
    if (refused?.success) return { completionRefused: refused.data.issues.map(issue => ({ field: issue.field, message: issue.message })) }
    throw error
  }
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyEBulkApplications') }) // Don't wait for this to complete
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') }) // Don't wait for this to complete

  // return redirect(`/applications/${result.eBulkApplicationId}`)
  return redirect(`/`)
}

// A clickable pencil icon
const EditButton: React.FC<{ onClick: () => void }> = ({ onClick }) =>
  <Button
    variant="text"
    size="small"
    sx={{ padding: 0, minWidth: 'auto', ml: 1 }}
    onClick={onClick}
  >
    ✏️
  </Button>

const DataField: React.FC<PropsWithChildren> = ({ children }) => {
  if (!children) {
    return <span style={{ color: 'red' }}>unknown</span>
  } else {
    return <span style={{ fontWeight: 'bold' }}>{children}</span>
  }
}

/** The messages of every validation error under the given field paths — a section's reasons for "fix section" */
const errorsUnder = (form: ReturnType<typeof useForm<CompleteFormSchema>>, prefixes: string[]): string[] =>
  flattenErrors(form.formState.errors)
    .filter(error => prefixes.some(prefix => error.path === prefix || error.path.startsWith(prefix + '.')))
    // Away from its field the message has to say what it is about: "Contact number: Must start with 0, …"
    .map(error => `${fieldLabel(error.path)}: ${error.message}`)

/** The data fields (not the confirmation tick-boxes) validated as soon as the page loads, so the sections can say what needs fixing before Continue is pressed */
const DATA_FIELDS = [
  'NiNumber', 'Gender', 'Title', 'Forename', 'Middlenames', 'PresentSurname', 'DateOfBirth', 'CurrentAddress', 'PreviousAddress', 'ApplicantIdentityDetails',
  'AdditionalApplicantDetails.ContactNumber', 'AdditionalApplicantDetails.LanguagePreference', 'AdditionalApplicantDetails.BirthSurname', 'AdditionalApplicantDetails.BirthSurnameUntil',
  'AdditionalApplicantDetails.OtherForenames', 'AdditionalApplicantDetails.OtherSurnames', 'AdditionalApplicantDetails.BirthTown', 'AdditionalApplicantDetails.BirthCounty',
  'AdditionalApplicantDetails.BirthCountry', 'AdditionalApplicantDetails.BirthNationality',
]

type FormProps = {
  application_id: string,
  getEBulkApplicationByID: ReturnType<typeof apiClientHooks.useGetEBulkApplicationByID<never, never, { data: LocalFormSchema }>>,
  form: ReturnType<typeof useForm<CompleteFormSchema>>,
}

const FormIdentity: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['NiNumber', 'Gender', 'AdditionalApplicantDetails.ContactNumber', 'AdditionalApplicantDetails.LanguagePreference'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/identity?${afterQueryParamKey}=submit`)

  console.log("FormIdentity: ", { error: form.getFieldState('AdditionalApplicantDetails') })

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Identity
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    <Typography variant="body1" marginBottom={1}>
      You have selected <DataField>{
        getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.LanguagePreference === undefined
          ? undefined
          : languagePreferenceMapping[getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.LanguagePreference].label
      }</DataField> as your preferred language for your application.
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      You have specified your gender as <DataField>{getEBulkApplicationByID.data?.data.Gender}</DataField>.
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      If you need to be contacted, your number is <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.ContactNumber}</DataField>.
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      Your National Insurance number is <DataField>{getEBulkApplicationByID.data?.data.NiNumber}</DataField>.
    </Typography>
  </>
}

const FormName: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['Title', 'Forename', 'Middlenames', 'PresentSurname', 'AdditionalApplicantDetails.BirthSurname', 'AdditionalApplicantDetails.BirthSurnameUntil', 'AdditionalApplicantDetails.OtherForenames', 'AdditionalApplicantDetails.OtherSurnames'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/names?${afterQueryParamKey}=submit`)

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Name
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    <Typography variant="body1" marginBottom={1}>
      Your current full name is:{' '}
      <DataField>{getEBulkApplicationByID.data?.data.Title}</DataField>{' '}
      <DataField>{getEBulkApplicationByID.data?.data.Forename}</DataField>{' '}
      {(getEBulkApplicationByID.data?.data.Middlenames?.Middlename.length ?? 0) > 0 && <>
        <DataField>{getEBulkApplicationByID.data?.data.Middlenames?.Middlename.join(' ')}</DataField>{' '}
      </>}
      <DataField>{getEBulkApplicationByID.data?.data.PresentSurname}</DataField>
    </Typography>
    <NameWarningsAlert names={getEBulkApplicationByID.data?.data} sx={{ marginBottom: 2 }} />
    {(getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurname?.length ?? 0) > 0 && <>
      <Typography variant="body1" marginBottom={1}>
        At birth, your surname was{' '}
        <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurname}</DataField>
        {getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurnameUntil
          ? <>
            {' '}until{' '}
            <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthSurnameUntil}</DataField>.
          </>
          : '.'}
      </Typography>
      <Typography variant="overline" marginBottom={1}>
      </Typography>
    </>}
    {(getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.OtherForenames?.OtherForename.length ?? 0) > 0 && <>
      <Typography variant="body1" marginBottom={1}>
        You have also been known by the following forenames:
      </Typography>
      {getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.OtherForenames?.OtherForename.map(otherForename => <>
        <Typography variant="overline" marginBottom={1}>
          <DataField>{otherForename.Name} (from {otherForename.UsedFrom} to {otherForename.UsedTo})</DataField>
        </Typography>
      </>)}
    </>}
    {(getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.OtherSurnames?.OtherSurname.length ?? 0) > 0 && <>
      <Typography variant="body1" marginBottom={1}>
        You have also been known by the following surnames:
      </Typography>
      {getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.OtherSurnames?.OtherSurname.map(otherSurname => <>
        <Typography variant="overline" marginBottom={1}>
          <DataField>{otherSurname.Name} (from {otherSurname.UsedFrom} to {otherSurname.UsedTo})</DataField>
        </Typography>
      </>)}
    </>}
    <Alert severity="info" sx={{ marginTop: 2, marginBottom: 3 }}>
      <CheckboxElement
        control={form.control}
        label="I have NOT been known by any other names during my lifetime"
        name="_noOtherNames"
      />
    </Alert>
  </>
}

const FormBirth: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['DateOfBirth', 'AdditionalApplicantDetails.BirthTown', 'AdditionalApplicantDetails.BirthCounty', 'AdditionalApplicantDetails.BirthCountry', 'AdditionalApplicantDetails.BirthNationality'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/birth?${afterQueryParamKey}=submit`)

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Birth
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    <Typography variant="body1" marginBottom={1}>
      You were born on <DataField>{getEBulkApplicationByID.data?.data.DateOfBirth}</DataField>.
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      You were born in <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthTown}</DataField>
      {getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthCounty
        ? <>
          {', '},
          <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthCounty}</DataField>,{' '}
        </>
        : ', '}
      <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthCountry}</DataField>.
    </Typography>
    {getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthNationality && <>
      <Typography variant="body1" marginBottom={1}>
        Your nationality is <DataField>{getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.BirthNationality}</DataField>.
      </Typography>
    </>}
  </>
}

const getYearsAndMonthsUsingDateFns = (fromDate: Date, toDate: Date = new Date()): string => {
  const years = datefns.differenceInYears(toDate, fromDate)
  const months = datefns.differenceInMonths(toDate, fromDate) % 12
  if (months === 0) return `${years} year${years === 1 ? '' : 's'}`
  if (years === 0) return `${months} month${months === 1 ? '' : 's'}`
  return `${years} year${years === 1 ? '' : 's'} and ${months} month${months === 1 ? '' : 's'}`
}

const FormAddress: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const {
    yearsSinceOldestAddress,
    needsMoreAddresses,
  } = useStartDatesOfAllAddresses(getEBulkApplicationByID.data?.data.CurrentAddress, getEBulkApplicationByID.data?.data.PreviousAddress)

  const currentAddressYearsAndMonths = getEBulkApplicationByID.data?.data.CurrentAddress?.ResidentFromGyearMonth
    ? getYearsAndMonthsUsingDateFns(new Date(getEBulkApplicationByID.data?.data.CurrentAddress?.ResidentFromGyearMonth))
    : undefined

  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['CurrentAddress', 'PreviousAddress'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/addresses?${afterQueryParamKey}=submit`)

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Addresses
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    <Typography variant="body1" marginBottom={1}>
      Since <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.ResidentFromGyearMonth}</DataField> (for the last <DataField>{currentAddressYearsAndMonths}</DataField>), you currently live at:
    </Typography>
    <Typography variant="overline" marginBottom={1}>
      <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressLine1}</DataField>{', '}
      {getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressLine2 != null && <>
        <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressLine2}</DataField>{', '}
      </>}
      <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressTown}</DataField>{', '}
      {getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressCounty != null && <>
        <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.AddressCounty}</DataField>{', '}
      </>}
      {getEBulkApplicationByID.data?.data.CurrentAddress?.Address.Postcode != null && <>
        <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.Postcode}</DataField>{', '}
      </>}
      <DataField>{getEBulkApplicationByID.data?.data.CurrentAddress?.Address.CountryCode}</DataField>
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      You have also lived at the following addresses:
    </Typography>
    {getEBulkApplicationByID.data?.data.PreviousAddress?.map((previousAddress, index) => <React.Fragment key={index}>
      <Typography variant="body1" marginBottom={1}>
        Between <DataField>{previousAddress.ResidentDates.ResidentFromGyearMonth}</DataField> and <DataField>{previousAddress.ResidentDates.ResidentToGyearMonth}</DataField> (for <DataField>{getYearsAndMonthsUsingDateFns(new Date(previousAddress.ResidentDates.ResidentFromGyearMonth), new Date(previousAddress.ResidentDates.ResidentToGyearMonth))}</DataField>) you lived at:
      </Typography>
      <Typography variant="overline" marginBottom={1}>
        <DataField>{previousAddress.Address.AddressLine1}</DataField>{', '}
        {previousAddress.Address.AddressLine2 != null && <>
          <DataField>{previousAddress.Address.AddressLine2}</DataField>{', '}
        </>}
        <DataField>{previousAddress.Address.AddressTown}</DataField>{', '}
        {previousAddress.Address.AddressCounty != null && <>
          <DataField>{previousAddress.Address.AddressCounty}</DataField>{', '}
        </>}
        {previousAddress.Address.Postcode != null && <>
          <DataField>{previousAddress.Address.Postcode}</DataField>{', '}
        </>
        }
        <DataField>{previousAddress.Address.CountryCode}</DataField>
      </Typography>
    </React.Fragment>)}
    {needsMoreAddresses
      ? <>
        <Alert severity="warning" sx={{ marginBottom: 3 }}>
          <AlertTitle>Needs Additional Address History</AlertTitle>
          You have covered {yearsSinceOldestAddress} of the required 5 years addresses.
        </Alert>
      </>
      : <>
        <Typography variant="body1" marginBottom={1}>
          You have supplied addresses for <DataField>{yearsSinceOldestAddress}</DataField> of the required 5 years.
        </Typography>
        <Alert severity="info" sx={{ marginBottom: 3 }}>
          <CheckboxElement
            control={form.control}
            label="I have NOT lived anywhere else during the last 5 years"
            name="_noOtherAddresses"
          />
        </Alert>
      </>}
  </>
}

const FormPassport: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['ApplicantIdentityDetails.PassportDetails'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/documents?${afterQueryParamKey}=submit`)

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Documents: Passport
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    {getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails
      ? <>
        <Typography variant="body1" marginBottom={1}>
          You have provided the following passport details:
        </Typography>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Passport Number</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails.PassportNumber}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Issue Date</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails.PassportIssueDate}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Nationality (on Passport)</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails.PassportNationality}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Date of Birth (on Passport)</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails.PassportDob}</DataField>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Alert severity="info" sx={{ marginBottom: 3 }}>
          <CheckboxElement
            control={form.control}
            label="My passport details (including name) are correct and my passport is NOT currently expired"
            name="_passportDetailsCorrect"
          />
        </Alert>
      </>
      : <>
        <Alert severity="info" sx={{ marginBottom: 3 }}>
          <CheckboxElement
            control={form.control}
            label="I do NOT have a valid passport matching my current full name"
            name="_passportDetailsCorrect"
          />
        </Alert>
      </>}
  </>
}

const FormDriverLicence: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Every error under this section's fields, so the applicant can see what to fix before jumping back
  const sectionErrors = errorsUnder(form, ['ApplicantIdentityDetails.DriverLicenceDetails'])
  const hasErrorsNeedingFixing = sectionErrors.length > 0
  const onClickFixSection = () => navigate(`/applications/${application_id}/documents?${afterQueryParamKey}=submit`)

  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Documents: Driver Licence
      <EditButton onClick={onClickFixSection} />
    </Typography>
    {hasErrorsNeedingFixing && (
      <Alert severity="error" sx={{ marginBottom: 3 }}>
        There are errors in this section that need fixing before you can continue.
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {sectionErrors.map((message, index) => <li key={index}>{message}</li>)}
        </ul>
        <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
      </Alert>
    )}
    {getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails
      ? <>
        <Typography variant="body1" marginBottom={1}>
          You have provided the following driver licence details:
        </Typography>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Driver Licence Number</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails.DriverLicenceNumber}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Licence Type</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails.DriverLicenceType}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Valid From</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails.DriverLicenceValidFrom}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Issuing Country</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails.DriverLicenceIssueCountry}</DataField>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Date of Birth (on Passport)</TableCell>
              <TableCell>
                <DataField>{getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails.DriverLicenceDOB}</DataField>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Alert severity="info" sx={{ marginBottom: 3 }}>
          <CheckboxElement
            control={form.control}
            label="My driver licence details (including name & address) are correct and my licence is NOT currently expired"
            name="_driverLicenceDetailsCorrect"
          />
        </Alert>
      </>
      : <>
        <Alert severity="info" sx={{ marginBottom: 3 }}>
          <CheckboxElement
            control={form.control}
            label="I do NOT have a valid driver licence matching my current full name and address"
            name="_driverLicenceDetailsCorrect"
          />
        </Alert>
      </>}
  </>
}

export const FormDocuments: React.FC<FormProps> = ({ application_id, getEBulkApplicationByID, form }) => {
  const navigate = useNavigate()

  // Check one of the fields from the page. If this one is valid, the others must be too, as they're checked upon page save.
  const passportNumber = getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.PassportDetails?.PassportNumber
  const driverLicenceNumber = getEBulkApplicationByID.data?.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber
  const alternativeDocumentsConfirmed = getEBulkApplicationByID.data?.data.NonApplicationFields?.NoPassportOrDriverLicence === true
  const noDocuments = !passportNumber && !driverLicenceNumber
  console.log("FormDocuments: ", { passportNumber, driverLicenceNumber: driverLicenceNumber, noDocuments })
  const onClickFixSection = () => navigate(`/applications/${application_id}/documents?${afterQueryParamKey}=submit`)

  return passportNumber || driverLicenceNumber
    ? <>
      <FormPassport {...{ application_id, getEBulkApplicationByID, form }} />
      <FormDriverLicence {...{ application_id, getEBulkApplicationByID, form }} />
    </>
    : alternativeDocumentsConfirmed
      ? <>
        <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
          Alternative Documents
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          You have confirmed you have one of the following valid documents:
        </Typography>
        <AlternativeValidDocuments />
      </>
      : <>
        <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
          Documents
        </Typography>
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          If you do not have a valid passport or driver licence, you must confirm you have other suitable documentation.
          <br />
          <Button variant="contained" sx={{ marginTop: 1 }} onClick={onClickFixSection}>Fix Section</Button>
        </Alert>
      </>
}

type SelectYesNoProps = {
  form: ReturnType<typeof useForm<CompleteFormSchema>>,
  name: FieldPath<CompleteFormSchema>,
}
const SelectYesNo: React.FC<SelectYesNoProps> = ({ form, name }) => {
  return <Controller
    control={form.control}
    name={name}
    render={({ field, fieldState: { error } }) => <Autocomplete
      options={['Yes', 'No']}
      disableClearable
      renderInput={(params) => <TextField {...params} error={!!error} helperText={error && error.message} />}
      value={field.value === 'y' ? 'Yes' : 'No'}
      onChange={(_, value) => {
        field.onChange(value === 'Yes' ? 'y' : 'n')
        form.trigger(name)
      }}
    />}
  />
}

export const FormUnspentConvictions: React.FC<FormProps> = ({ form }) => {
  const unspentConvictions = form.watch('AdditionalApplicantDetails.UnspentConvictions')
  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Unspent Convictions
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      Do you have any convictions, cautions, reprimands or final warnings which would not be filtered in line with current guidance?
    </Typography>
    <SelectYesNo form={form} name="AdditionalApplicantDetails.UnspentConvictions" />
    {unspentConvictions === 'y' && <>
      <Alert severity="warning" sx={{ marginTop: 2, marginBottom: 3 }}>
        <AlertTitle>Disclosure Recommendation</AlertTitle>
        Having convictions <strong>does not</strong> prevent you from obtaining a DBS check, but we advise that you voluntarily disclose these to your employer, as your DBS certificate will show them.
      </Alert>
    </>}
  </>
}

export const FormPrivacyPolicy: React.FC<FormProps> = ({ form }) => {
  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      DBS Privacy Notice
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      I have read the Privacy notice for DBS Disclosure Services <a href="https://www.gov.uk/government/publications/dbs-disclosure-services-privacy-notice" target="_blank" rel="noopener noreferrer">DBS Disclosure Services Privacy Notice - GOV.UK</a> and I understand how DBS will process my personal data and the options available to me for submitting an application
    </Typography>
    <Alert severity="info" sx={{ marginBottom: 3 }}>
      <CheckboxElement
        control={form.control}
        label="I confirm the above"
        name="_privacyPolicyConfirmed"
      />
    </Alert>
  </>
}

export const FormDeclarationByApplicant: React.FC<FormProps> = ({ form }) => {
  return <>
    <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
      Applicant Declaration
    </Typography>
    <Typography variant="body1" marginBottom={1}>
      I, the applicant, declare that I have provided complete and true information and that I understand that knowingly making a false statement for this purpose is a criminal offence.
    </Typography>
    <SelectYesNo form={form} name="AdditionalApplicantDetails.DeclarationByApplicant" />
  </>
}

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })

  // Build the Form
  const form = useForm<CompleteFormSchema>({
    resolver: zodResolver(zCompleteFormSchema),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      _noOtherNames: false,
      _noOtherAddresses: false,
      _passportDetailsCorrect: false,
      _driverLicenceDetailsCorrect: false,
      // Defaults for the confirmation fields
      AdditionalApplicantDetails: {
        ...getEBulkApplicationByID.data?.data.AdditionalApplicantDetails ?? {},
        UnspentConvictions: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.UnspentConvictions ?? 'n',
        DeclarationByApplicant: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.DeclarationByApplicant ?? 'n',
      },
      // These are needed to ensure the form is valid, but are ignored on the server
      ApplicantIdentityDetails: {
        ...getEBulkApplicationByID.data?.data.ApplicantIdentityDetails ?? {},
        EvidenceCheckedBy: 'UNCHECKED',
        IdentityVerified: 'n',
      },
    },
  })

  // Validate the data (not the tick-boxes) as soon as it is loaded, so each section can say what needs fixing —
  // including cross-page checks the pages themselves only warn about (DBS-264) — before Continue is pressed
  const loadedData = getEBulkApplicationByID.data?.data
  useEffect(() => {
    if (loadedData !== undefined) form.trigger(DATA_FIELDS as any)
  }, [form, loadedData])

  console.log("ApplicationSubmit.Element: ", form.formState, form.getFieldState(''))

  // DBS-305: the server's reasons when it refused to complete (normally the checks on this page catch them first)
  const completionRefused = (useActionData() as { completionRefused?: { field: string, message: string }[] } | undefined)?.completionRefused

  return <>
    <Helmet>
      <title>Review DBS Application</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h1" variant="h5" marginBottom={3}>
          Review DBS Application
        </Typography>
        {completionRefused !== undefined && (
          <Alert severity="error" sx={{ marginBottom: 3, width: '100%' }} data-testid="completion-refused">
            <AlertTitle>Your application could not be submitted yet</AlertTitle>
            Please correct the following, then submit again:
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {completionRefused.map((issue, index) => <li key={index}><strong>{fieldLabel(issue.field)}</strong>: {issue.message}</li>)}
            </ul>
          </Alert>
        )}
        <Typography variant="body1" marginBottom={1}>
          Please carefully review the information you have entered on your application.
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          Pay attention to the confirmation statements as you will need to declare that this is a true and faithful statement of fact about you.
          {/* TODO: Check that this is a statement of fact! */}
        </Typography>

        <Form method="post">
          <FormIdentity {...{ application_id, getEBulkApplicationByID, form }} />
          <FormName {...{ application_id, getEBulkApplicationByID, form }} />
          <FormBirth {...{ application_id, getEBulkApplicationByID, form }} />
          <FormAddress {...{ application_id, getEBulkApplicationByID, form }} />
          <FormDocuments {...{ application_id, getEBulkApplicationByID, form }} />
          <FormUnspentConvictions {...{ application_id, getEBulkApplicationByID, form }} />
          <FormPrivacyPolicy {...{ application_id, getEBulkApplicationByID, form }} />
          <FormDeclarationByApplicant {...{ application_id, getEBulkApplicationByID, form }} />
          <Alert severity="warning" sx={{ marginTop: 3 }}>
            <AlertTitle>Important</AlertTitle>
            Once you continue, you will not be able to change any of the information above. If you have not filled in the form correctly, <strong>you may incur a charge</strong> for a new application.
          </Alert>
          <TestingInfoBlockFormErrors form={form} />
          <input type="hidden" name="eBulkApplicationJSON" value={JSON.stringify(form.getValues())} />
          <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginTop: 3 }} />
          <FormButtons form={form} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
